import type { ConfirmationContentProps } from "./ClientTools";
import styles from "./CreateEventsConfirmation.module.css";

interface EventAssociation {
  dataset_id?: string;
  file_id?: string;
  topic_id?: string;
  message_path_id?: string;
}

interface EventParams {
  name?: string;
  description?: string;
  start_time?: number;
  end_time?: number;
  associations?: EventAssociation[];
  tags?: string[];
  display_options?: { color?: string };
}

/**
 * Format a nanosecond timestamp into a human-readable datetime string.
 */
function formatNanoseconds(ns: number): string {
  const ms = ns / 1_000_000;
  const date = new Date(ms);

  if (isNaN(date.getTime())) {
    return String(ns);
  }

  return date.toISOString().replace("T", " ").replace("Z", " UTC");
}

/**
 * Summarize associations into a short display string.
 */
function formatAssociations(associations: EventAssociation[]): string {
  return associations
    .map((a) => {
      if (a.dataset_id) {
        return `Dataset: ${a.dataset_id}`;
      }
      if (a.file_id) {
        return `File: ${a.file_id}`;
      }
      if (a.topic_id) {
        return `Topic: ${a.topic_id}`;
      }
      if (a.message_path_id) {
        return `Message Path: ${a.message_path_id}`;
      }
      return "Unknown";
    })
    .join(", ");
}

/**
 * Read-only preview of the events the LLM wants to create.
 * Rendered inside the ToolConfirmationModal.
 */
export function CreateEventsConfirmation({ params }: ConfirmationContentProps) {
  const rawEvents = params.events;
  const events = (Array.isArray(rawEvents) ? rawEvents : []) as EventParams[];

  if (events.length === 0) {
    return <div className={styles.empty}>No events to create.</div>;
  }

  return (
    <div className={styles.container}>
      {events.map((event, index) => (
        <div key={index} className={styles.eventCard}>
          <div className={styles.eventName}>
            {event.display_options?.color && (
              <span
                className={styles.colorSwatch}
                style={{ backgroundColor: event.display_options.color }}
              />
            )}
            {event.name ?? "Unnamed Event"}
          </div>

          {event.description && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Description</span>
              <span className={styles.fieldValue}>{event.description}</span>
            </div>
          )}

          {event.start_time !== undefined && event.end_time !== undefined && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Time</span>
              <span className={styles.fieldValue}>
                {formatNanoseconds(event.start_time)}
                {event.start_time !== event.end_time &&
                  ` → ${formatNanoseconds(event.end_time)}`}
              </span>
            </div>
          )}

          {event.associations && event.associations.length > 0 && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Associations</span>
              <span className={styles.fieldValue}>
                {formatAssociations(event.associations)}
              </span>
            </div>
          )}

          {event.tags && event.tags.length > 0 && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Tags</span>
              <span className={styles.fieldValue}>{event.tags.join(", ")}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
